import { AxiosError } from 'axios';
import isEmpty from 'lodash/isEmpty';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Row, Col } from 'reactstrap';

import { IRootState } from '@app/store';
import { updateEmailOrMobileRequest, sendVerificationCodeRequest } from '@reducers/auth/AuthAction';
import { handleError } from '@services/ErrorHandler';
import { CustomButton } from '@shared/buttons';
import InputField from '@shared/components/input/InputField';
import { VERIFICATION_TYPES } from '@shared/constants';
import useTranslation from '@shared/hooks/useTranslation';
import {
  CloseIcon,
} from '@shared/icons';

import '../VerificationModalStyle.scss';

type IVerificationContent = {
  onNext: () => void;
  onToggle: () => void;
  type?: string;
};

const VerificationContent = ({ onNext, onToggle, type }: IVerificationContent) => {
  const i18nVerification = useTranslation('verification.account.modal');
  const sendVerification = useTranslation('verification.button.send_verification');
  const userDetails = useSelector((state: IRootState) => state.Auth.user);
  const userEmail = userDetails?.email;
  const userMobile = userDetails?.mobile_number ? userDetails?.mobile_number?.replace('+1', '') : '';
  const currentUserEmailOrMobile = type === 'email' ? userEmail : userMobile;
  const [ emailOrMobile, setEmailOrMobile ] = useState(type === 'email' ? userEmail : userMobile);
  const [ isSending, setSending ] = useState(false);
  const [ errorMessage, setErrorMessage ] = useState('');
  const dispatch = useDispatch<any>();

  const handleVerify = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSending(true);
    setErrorMessage('');
    try {
      // if the user enter's a different mobile or email, update the email or mobile
      if (currentUserEmailOrMobile !== emailOrMobile) {
        await dispatch(updateEmailOrMobileRequest(type, {
          old_email_or_mobile: type === 'email' ? userEmail : userMobile,
          email_or_mobile: emailOrMobile,
          payload_type: type,
        })).$promise;
      }
      await dispatch(sendVerificationCodeRequest({
        emailOrMobile,
        email_or_mobile: emailOrMobile,
        type: VERIFICATION_TYPES.registration,
        payload_type: type,
      })).$promise;
      onNext();
    } catch (error) {
      const { response: errorResponse } = error as AxiosError;
      if (errorResponse) {
        setErrorMessage(errorResponse.data?.message);
        return;
      }
      handleError(error);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="verification-content-container">
      <Row className="mb-3">
        <Col className="verify-close-button-style">
          <a aria-label="Close" role="button" href="#" onClick={onToggle}><CloseIcon /></a>
        </Col>
      </Row>
      <Row>
        <Col>
          <div className="s2 verification-content-modal-title mb-5">
            {type === 'email' ? i18nVerification.email.title : i18nVerification.mobile.title}
          </div>
          <div className="b1 mb-2">
            {type === 'email' ? i18nVerification.email.description : i18nVerification.mobile.description}
          </div>
          <Form id="verification-form" onSubmit={handleVerify}>
            {
              type === 'email' ? (
                <InputField
                  id="verification-email"
                  label={i18nVerification.email.input.label}
                  defaultValue={emailOrMobile}
                  placeholder={i18nVerification.email.input.placeholder}
                  onInputChangeHandler={(value) => setEmailOrMobile(value)}
                  type="email"
                  name="emailOrMobile"
                  value={emailOrMobile}
                  fieldError={errorMessage}
                />
              ) : (
                <InputField
                  id="verification-mobile"
                  name="emailOrMobile"
                  label={i18nVerification.mobile.input.label}
                  placeholder={i18nVerification.mobile.input.placeholder}
                  type="text"
                  required
                  value={emailOrMobile}
                  fieldError={errorMessage}
                  onInputChangeHandler={(value) => setEmailOrMobile(value)}
                  defaultValue={emailOrMobile}
                  isMobile
                  mobileCountryCode="+1"
                  customClassName="custom-mobile-number-form-padding"
                  formGrpClassName="verification-mobile-form-group"
                />
              )
            }
            <CustomButton
              id="btn-send"
              loading={isSending}
              disabled={isSending || isEmpty(emailOrMobile)}
              color="primary"
              className="btn-lg"
              type="submit"
            >
              {sendVerification}
            </CustomButton>
          </Form>
        </Col>
      </Row>
    </div>
  );
};

export default VerificationContent;
